import React, { useEffect, useState } from "react";
import "./index.scss";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import * as _ from "lodash";
import * as Moment from "moment";
import Table from "../../../../../components/core/table";
import Dropdown from "../../../../../components/core/dropdown";
import { useTranslation } from "react-i18next";
import {
  changeProfileTriggerPosition,
  listProfileTriggerByProfileId,
  removeProfileTrigger,
} from "../../../../../store/profile-trigger/thunkAction";
import {
  sendNotifyIziMessageError,
  sendNotifyIziMessageSuccess,
} from "../../../../../utils/event";

const ProfileTrigger = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const params = useParams();
  const profileId = _.get(params, "id");
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(null);
  const optionStatus = [
    { id: true, name: t("common.active") },
    { id: false, name: t("common.inactive") },
  ];

  const profileTriggerManage = useSelector(
    (state) => state.profileTriggerManage
  );
  const triggers = _.get(profileTriggerManage, "triggers", []);
  const isLoadingChangeProfileTriggerPosition = _.get(
    profileTriggerManage,
    "isLoadingChangeProfileTriggerPosition",
    false
  );

  useEffect(() => {
    const filter = {
      search,
      status,
    };

    if (!search) {
      dispatch(listProfileTriggerByProfileId({ profileId, filter }));

      return;
    }

    const searchTimeout = setTimeout(() => {
      dispatch(listProfileTriggerByProfileId({ profileId, filter }));
    }, 1000);

    return () => clearTimeout(searchTimeout);
  }, [dispatch, profileId, search, status]);

  const onSelectStatus = (option) => {
    setStatus(_.get(option, "id", null));
  };

  const removeTrigger = async (trigger) => {
    const triggerId = _.get(trigger, "_id");

    if (!triggerId) return;

    try {
      await dispatch(removeProfileTrigger(triggerId)).unwrap();
      sendNotifyIziMessageSuccess(t("profile.profile_trigger_remove_success"));
    } catch (error) {
      sendNotifyIziMessageError(t("profile.profile_trigger_remove_error"));
    }
    const filter = {
      search,
      status,
    };

    dispatch(listProfileTriggerByProfileId({ profileId, filter }));
  };

  const onSortEnd = async (options) => {
    const firstId = _.get(options, "sourceId", null);
    const secondId = _.get(options, "destinationId", null);

    if (isLoadingChangeProfileTriggerPosition) {
      return;
    }

    try {
      await dispatch(
        changeProfileTriggerPosition({
          firstId,
          secondId,
        })
      ).unwrap();

      const filter = {
        search,
        status,
      };
      dispatch(listProfileTriggerByProfileId({ profileId, filter }));
      sendNotifyIziMessageSuccess(t("profile.profile_trigger_save_success"));
    } catch (error) {
      sendNotifyIziMessageError(t("profile.profile_trigger_save_error"));
    }
  };

  const configTable = {
    header: [
      {
        key: "no",
        label: "NO.",
        className: "text-center small",
        render: (row, rowIndex) => rowIndex + 1,
      },
      {
        key: "name",
        label: t("settings.profile.profile_trigger.trigger_name"),
        className: "small",
        render: (row) => {
          const name = _.get(row, "name");

          return name;
        },
      },
      {
        key: "conditions",
        label: t("settings.profile.profile_trigger.conditions"),
        className: "small",
        render: (row) => {
          const allConditions = _.get(row, "allConditions", []);
          const anyConditions = _.get(row, "anyConditions", []);

          return `${t("settings.profile.profile_trigger.all_conditions")} ${
            allConditions.length
          }, ${t("settings.profile.profile_trigger.any_conditions")} ${
            anyConditions.length
          }`;
        },
      },
      {
        key: "actions",
        label: t("settings.profile.profile_trigger.actions"),
        className: "small",
        render: (row) => {
          const actions = _.get(row, "actions", []);

          return `${actions.length}`;
        },
      },
      {
        key: "lastUpdated",
        label: t("settings.profile.profile_trigger.last_update"),
        className: "small",
        render: (row) => {
          let result = "";
          const lastUpdateTime = _.get(row, "lastUpdateTime");
          const now = Moment();
          const lastUpdate = Moment(lastUpdateTime);

          const duration = Moment.duration(now.diff(lastUpdate));
          const days = duration.days();
          const hours = duration.hours();
          const minutes = duration.minutes();

          if (days) result += `${days} days, `;
          if (hours) result += `${hours} hours, `;
          if (minutes) result += `${minutes} minutes `;

          result += !days && !hours && !minutes ? "a few seconds ago" : "ago";

          return result;
        },
      },
      {
        key: "createdAt",
        label: t("settings.profile.profile_trigger.created_at"),
        className: "small",
        render: (row) => {
          const addTime = _.get(row, "addTime");

          return Moment(addTime).format("HH:mm - DD.MM.YYYY");
        },
      },
      {
        key: "status",
        label: t("settings.profile.profile_trigger.status"),
        className: "text-center small",
        render: (row) => {
          const isActive = _.get(row, "isActive", false);

          return (
            <div style={{ justifyContent: "center" }} className="d-flex gap-4">
              <div className="w-125px">
                {isActive ? (
                  <span className="btn btn-success-light btn-sm">
                    {t("common.active")}
                  </span>
                ) : (
                  <span className="btn btn-danger-light btn-sm">
                    {t("common.inactive")}
                  </span>
                )}
              </div>
            </div>
          );
        },
      },
      {
        key: "manage",
        label: t("settings.profile.profile_trigger.manage"),
        className: "text-center small",
        render: (row) => {
          return (
            <div className="d-flex gap-4" style={{ justifyContent: "center" }}>
              <button
                type="button"
                className="icon-button"
                onClick={() => {
                  navigate(
                    `/profile/${profileId}/trigger/edit/trigger-id/${row._id}`
                  );
                }}
              >
                <i className="fa fa-pencil-alt"></i>
              </button>
              <button
                style={{ background: "none", border: "none" }}
                className="color-red"
                onClick={() => removeTrigger(row)}
              >
                <i className="fa fa-trash-alt"></i>
              </button>
            </div>
          );
        },
      },
    ],
    collection: triggers,
    isSortTable: true,
    onSortEnd,
  };

  return (
    <div className="container">
      <div className="card bg-white border-0">
        <div className="card-body">
          <div className="row header-title mb-4">
            <div className="col-lg-6">
              <h3 className="color-pri">
                {t("settings.profile.profile_trigger.title_trigger")}
              </h3>
            </div>
            <div className="col-lg-6 text-start text-lg-end align-self-center">
              <a
                className="default-link"
                href={`#/profile/${profileId}/detail`}
              >
                <i className="fa fa-arrow-left"></i>{" "}
                {t("settings.profile.profile_detail")}
              </a>
            </div>
            <div className="col-12 color-gray small">
              {t("settings.profile.profile_trigger.desc")}
            </div>
          </div>

          <div className="row mb-3 g-3">
            <div className="col-lg-4">
              <div className="input-icon">
                <i className="fa fa-search"></i>
                <input
                  type="text"
                  className="form-control has-icon search"
                  placeholder={t("settings.profile.profile_trigger.search")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="col-lg-2">
              <Dropdown
                config={{
                  options: optionStatus,
                  onSelect: onSelectStatus,
                  placeholder: t("settings.profile.profile_trigger.status"),
                  value: status,
                  className: "button-status",
                }}
              />
            </div>
            <div className="col-lg-2"></div>
            <div className="col-lg-2 text-end">
              <div className="dropdown-center">
                <button
                  className="btn btn-gray btn-outline-gray dropdown-toggle dropdown-toggle-tips"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <i className="far fa-lightbulb"></i>
                </button>
                <div className="dropdown-menu dropdown-menu-big p-3 text-center">
                  <img className="mb-3" src="/img/chat/tip.png" alt="" />
                  <div className="text-start small">
                    <strong>Drag &amp; drop to reorder active triggers:</strong>{" "}
                    Drag and drop triggers to change their execution order. The
                    system will process triggers sequentially, starting from the
                    top.
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-2">
              <a
                href={`#/profile/${profileId}/trigger/add`}
                className="btn btn-primary w-100"
              >
                {t("common.create")}
              </a>
            </div>
          </div>
          <div className="table-responsive table-box mb-5">
            <Table config={configTable} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileTrigger;
